/**
 * SystemOptionsDisplay Component
 *
 * Displays filtered system options (top 3 applicable).
 * For gas systems, filters by condensing capability.
 * Shows each option with savings, rebates, and financial analysis.
 */

import { useState, useEffect } from 'react';
import SystemOptionCard from './SystemOptionCard';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import {
  filterSystemOptionsByConstraints,
  calculateOptionSavings
} from '../../../utils/systemOptionCalculations';
import { matchRebatesForOption } from '../../../utils/rebateMatching';

export default function SystemOptionsDisplay({
  systemType,
  canInstallCondensing,
  currentAnnualEnergyCost,
  currentAge,
  squareFootage,
  climateData,
  envelope,
  electricRate,
  gasRate,
  assessmentData
}) {
  const { contractor } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systemOptions, setSystemOptions] = useState([]);
  const [allRebates, setAllRebates] = useState([]);
  const [analyzedOptions, setAnalyzedOptions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Carousel navigation functions
  const goToNext = () => {
    if (currentIndex < analyzedOptions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToIndex = (index) => {
    setCurrentIndex(index);
  };

  // Load system options and rebates
  useEffect(() => {
    if (contractor?.id && systemType) {
      loadData();
    }
  }, [contractor, systemType, canInstallCondensing]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load system options
      const { data: options, error: optionsError } = await supabase
        .from('system_options')
        .select('*')
        .eq('contractor_id', contractor.id)
        .eq('system_type', systemType)
        .eq('is_enabled', true)
        .order('option_number');

      if (optionsError) throw optionsError;

      // Filter options by condensing capability
      const filteredOptions = filterSystemOptionsByConstraints(
        options || [],
        systemType,
        canInstallCondensing
      );

      setSystemOptions(filteredOptions);

      // Load rebates
      const { data: rebates, error: rebatesError } = await supabase
        .from('rebates')
        .select('*')
        .eq('contractor_id', contractor.id)
        .eq('status', 'active');

      if (rebatesError) throw rebatesError;
      setAllRebates(rebates || []);

      // Analyze each option
      if (filteredOptions.length > 0) {
        const analyzed = filteredOptions.map((option, index) => {
          // Calculate savings
          const savings = calculateOptionSavings({
            systemOption: option,
            currentAnnualEnergyCost,
            currentAge,
            systemType,
            squareFootage,
            climateData,
            envelope,
            electricRate,
            gasRate,
            yearsToProject: 10
          });

          // Match rebates for this option
          const rebateGroups = matchRebatesForOption({
            systemOption: option,
            allRebates: rebates || [],
            assessmentData,
            contractorJurisdictions: [] // Would come from contractor settings
          });

          return {
            option,
            savings,
            rebateGroups,
            rank: index + 1
          };
        });

        // Sort by total benefit (highest first)
        analyzed.sort((a, b) => {
          const aTotal = a.savings.totalTenYearBenefit + (a.rebateGroups[0]?.totalAmount || 0);
          const bTotal = b.savings.totalTenYearBenefit + (b.rebateGroups[0]?.totalAmount || 0);
          return bTotal - aTotal;
        });

        setAnalyzedOptions(analyzed);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading system options:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '16px', color: '#64748b' }}>
          Loading system options...
        </div>
      </div>
    );
  }

  if (analyzedOptions.length === 0) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '25px'
      }}>
        <div style={{
          padding: '20px',
          background: '#fef3c7',
          borderRadius: '8px',
          border: '2px solid #fcd34d',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
            No System Options Configured
          </div>
          <div style={{ fontSize: '14px', color: '#92400e' }}>
            Please configure system options in Settings → Systems to see recommendations.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '25px'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #3b82f6',
        paddingBottom: '15px',
        marginBottom: '25px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#3b82f6',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          Your System Options
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '24px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          Replacement Solutions
        </h3>
        <p style={{
          margin: '8px 0 0 0',
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          Here are {analyzedOptions.length} system option{analyzedOptions.length > 1 ? 's' : ''} that would solve your comfort issues
          while delivering significant energy and maintenance savings. All options are professionally installed
          and backed by manufacturer warranties.
        </p>
      </div>

      {/* Filtering Info (for gas systems) */}
      {['furnaceAC', 'furnaceACPackage', 'furnaceOnly', 'boiler'].includes(systemType) && canInstallCondensing && (
        <div style={{
          padding: '15px 20px',
          background: '#fffbeb',
          borderRadius: '8px',
          border: '1px solid #fcd34d',
          marginBottom: '25px',
          fontSize: '13px',
          color: '#92400e'
        }}>
          {canInstallCondensing === 'yes' && (
            <div>
              ✓ Showing <strong>condensing furnace</strong> options (90%+ AFUE) based on home compatibility
            </div>
          )}
          {canInstallCondensing === 'no' && (
            <div>
              ✓ Showing <strong>conventional furnace</strong> options (80% AFUE) based on home compatibility
            </div>
          )}
          {canInstallCondensing === 'unsure' && (
            <div>
              ⚠️ Showing <strong>all furnace options</strong>. Your comfort consultant will determine which type works best for your home.
            </div>
          )}
        </div>
      )}

      {/* Carousel Container */}
      <div style={{
        position: 'relative',
        marginBottom: '30px',
        paddingTop: '20px' // Space for Best Value badge
      }}>
        {/* Carousel Wrapper */}
        <div style={{
          overflow: 'visible', // Changed to visible to show badge
          position: 'relative',
          paddingBottom: '20px'
        }}>
          {/* Inner Wrapper with Hidden Overflow */}
          <div style={{
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Carousel Track */}
            <div style={{
              display: 'flex',
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: `translateX(calc(-${currentIndex} * (100% - 120px + 20px) + 60px))`,
              gap: '20px'
            }}>
              {analyzedOptions.map((analyzed, index) => (
                <div
                  key={analyzed.option.id}
                  style={{
                    minWidth: 'calc(100% - 120px)',
                    flex: '0 0 calc(100% - 120px)',
                    opacity: index === currentIndex ? 1 : 0.4,
                    transform: index === currentIndex ? 'scale(1)' : 'scale(0.95)',
                    transition: 'opacity 0.4s ease, transform 0.4s ease'
                  }}
                >
                  <SystemOptionCard
                    option={analyzed.option}
                    savings={analyzed.savings}
                    rebateGroups={analyzed.rebateGroups}
                    isRecommended={index === 0} // First option (highest benefit) is recommended
                    rank={analyzed.rank}
                    financing={{
                      enabled: contractor?.financing_enabled || false,
                      url: contractor?.financing_url || ''
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        {analyzedOptions.length > 1 && (
          <>
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: 'none',
                background: currentIndex === 0
                  ? 'rgba(148, 163, 184, 0.3)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '24px',
                cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                boxShadow: currentIndex === 0 ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'all 0.2s ease',
                opacity: currentIndex === 0 ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (currentIndex !== 0) {
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentIndex !== 0) {
                  e.target.style.transform = 'translateY(-50%) scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }
              }}
            >
              ←
            </button>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              disabled={currentIndex === analyzedOptions.length - 1}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: 'none',
                background: currentIndex === analyzedOptions.length - 1
                  ? 'rgba(148, 163, 184, 0.3)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '24px',
                cursor: currentIndex === analyzedOptions.length - 1 ? 'not-allowed' : 'pointer',
                boxShadow: currentIndex === analyzedOptions.length - 1 ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'all 0.2s ease',
                opacity: currentIndex === analyzedOptions.length - 1 ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (currentIndex !== analyzedOptions.length - 1) {
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentIndex !== analyzedOptions.length - 1) {
                  e.target.style.transform = 'translateY(-50%) scale(1)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }
              }}
            >
              →
            </button>
          </>
        )}

        {/* Dot Indicators */}
        {analyzedOptions.length > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '20px'
          }}>
            {analyzedOptions.map((_, index) => (
              <button
                key={index}
                onClick={() => goToIndex(index)}
                style={{
                  width: index === currentIndex ? '32px' : '12px',
                  height: '12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: index === currentIndex
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : '#cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  padding: 0,
                  boxShadow: index === currentIndex ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (index !== currentIndex) {
                    e.target.style.background = '#94a3b8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (index !== currentIndex) {
                    e.target.style.background = '#cbd5e1';
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Educational Footer */}
      <div style={{
        marginTop: '25px',
        padding: '15px 20px',
        background: '#f0f9ff',
        borderRadius: '8px',
        border: '1px solid #bfdbfe'
      }}>
        <div style={{
          fontSize: '13px',
          color: '#1e40af',
          lineHeight: '1.6'
        }}>
          <strong>💡 Note on Pricing:</strong> These are estimated starting prices for informational purposes.
          Your comfort consultant will provide an exact quote based on your specific installation requirements,
          including any necessary ductwork modifications, electrical upgrades, or permit fees.
        </div>
      </div>
    </div>
  );
}
